<template>
  <div class="mh-page">
    <header class="home-header">
      <div class="brand"><img src="@/assets/logo1.png" alt="MH13" /></div>
      <div class="header-actions">
        <button @click="$router.push('/profile?section=notifications')">
          <i class="bi bi-bell"></i
          ><b v-if="notificationCount">{{ notificationCount }}</b></button
        ><router-link to="/profile"
          ><span>{{ profileInitial }}</span></router-link
        >
      </div>
    </header>
    <main class="mh-content">
      <section class="welcome">
        <div class="sun">{{ greetingIcon }}</div>
        <div>
          <h1>
            {{ greeting }}<span v-if="profileName">, {{ profileName }}</span> 👋
          </h1>
          <p>Here's what's happening in {{ city }}</p>
        </div>
        <div v-if="temperature" class="weather">
          <strong>{{ temperature }}°C</strong><small>Partly Cloudy</small>
        </div>
      </section>
      <section class="mh-section">
        <StorySlider
          v-if="!loading.stories"
          :stories="stories"
          @select="openCategory"
        />
        <AppShimmer v-else type="stories" :count="6" />
      </section>
      <section class="mh-section">
        <div class="mh-section-head">
          <h2 class="mh-section-title"><i class="bi bi-fire"></i> For You</h2>
          <div class="view-toggle">
            <button
              :class="{ active: viewMode === 'card' }"
              @click="changeView('card')"
            >
              <i class="bi bi-grid"></i></button
            ><button
              :class="{ active: viewMode === 'list' }"
              @click="changeView('list')"
            >
              <i class="bi bi-list"></i>
            </button>
          </div>
        </div>
        <AppShimmer
          v-if="loading.feed && !feed.length"
          :mode="viewMode"
          :count="4"
        />
        <div v-else class="feed">
          <ListingCard
            v-for="item in feed"
            :key="item.type + '-' + (item.id || item.link)"
            :item="item"
            :type="item.type"
            :mode="viewMode"
            @open="openListing"
            @share="shareListing"
          />
        </div>
        <EmptyState
          v-if="!feed.length && !loading.feed"
          title="No updates yet"
        />
      </section>
      <HomeStrip
        title="Sponsored"
        :items="sponsored"
        type="business"
        :loading="loading.sponsored"
        @open="openListing"
      />
      <HomeStrip
        title="Offers Near You"
        :items="offers"
        type="business"
        :loading="loading.offers"
        @open="openListing"
      />
      <HomeStrip
        title="Latest Jobs"
        :items="jobs"
        type="job"
        :loading="loading.jobs"
        @open="openListing"
      />
      <HomeStrip
        title="Upcoming Events"
        :items="events"
        type="event"
        :loading="loading.events"
        @open="openListing"
      />
      <HomeStrip
        title="Popular Businesses"
        :items="businesses"
        type="business"
        :loading="loading.businesses"
        @open="openListing"
      />
      <section v-if="recentlyViewed.length" class="mh-section">
        <div class="mh-section-head">
          <h2 class="mh-section-title">Recently Viewed</h2>
          <span class="mh-link">{{ recentlyViewed.length }} items</span>
        </div>
        <div class="feed">
          <ListingCard
            v-for="item in recentlyViewed.slice(0, 4)"
            :key="'r-' + item.type + '-' + item.id"
            :item="item"
            :type="item.type"
            mode="list"
            @open="openListing"
          />
        </div>
      </section>
    </main>
    <BottomNavigation />
  </div>
</template>
<script>
import api from "@/api";
import {
  addRecentlyViewed,
  getListingView,
  getRecentlyViewed,
  getStored,
  setListingView,
  setStored,
} from "@/utils/storage";
import BottomNavigation from "@/components/BottomNavigation.vue";
import StorySlider from "@/components/StorySlider.vue";
import ListingCard from "@/components/ListingCard.vue";
import EmptyState from "@/components/EmptyState.vue";
import AppShimmer from "@/components/AppShimmer.vue";
import HomeStrip from "@/components/HomeStrip.vue";
export default {
  name: "HomeView",
  components: {
    BottomNavigation,
    StorySlider,
    ListingCard,
    EmptyState,
    AppShimmer,
    HomeStrip,
  },
  data() {
    return {
      city: getStored("last_selected_location", "Solapur"),
      profile: [],
      temperature: "",
      notificationCount: 0,
      viewMode: getListingView(),
      recentlyViewed: getRecentlyViewed(),
      stories: [],
      feed: [],
      jobs: [],
      events: [],
      businesses: [],
      offers: [],
      sponsored: [],
      loading: {
        header: true,
        stories: true,
        feed: true,
        jobs: true,
        events: true,
        businesses: true,
        offers: true,
        sponsored: true,
      },
    };
  },
  computed: {
    profileName() {
      return this.profile?.name || this.profile?.[0]?.name || "";
    },
    profileInitial() {
      return (this.profileName || "M").charAt(0).toUpperCase();
    },
    hour() {
      return Number(
        new Intl.DateTimeFormat("en-IN", {
          hour: "2-digit",
          hour12: false,
          timeZone: "Asia/Kolkata",
        }).format(new Date()),
      );
    },
    greeting() {
      if (this.hour < 12) return "Good Morning";
      if (this.hour < 17) return "Good Afternoon";
      if (this.hour < 21) return "Good Evening";
      return "Good Night";
    },
    greetingIcon() {
      if (this.hour < 12) return "🌅";
      if (this.hour < 17) return "☀️";
      if (this.hour < 21) return "🌇";
      return "🌙";
    },
  },
  mounted() {
    this.restoreScroll();
    this.loadSection("header", "home_header", api.getHomeHeader, (d) => {
      this.profile = d.profile || [];
      this.temperature = d.temperature || "";
      this.city = getStored("last_selected_location", d.city || "Solapur");
    });
    this.loadSection(
      "stories",
      "home_stories_v3",
      api.getHomeStories,
      (d) => (this.stories = this.mapStories(d)),
    );
    this.loadSection(
      "feed",
      "home_feed_v3",
      api.getHomeFeed,
      (d) => (this.feed = d || []),
    );
    this.loadSection(
      "jobs",
      "home_jobs",
      api.getHomeJobs,
      (d) => (this.jobs = d || []),
    );
    this.loadSection(
      "events",
      "home_events",
      api.getHomeEvents,
      (d) => (this.events = d || []),
    );
    this.loadSection(
      "businesses",
      "home_businesses",
      api.getHomeBusinesses,
      (d) => (this.businesses = d || []),
    );
    this.loadSection(
      "offers",
      "home_offers",
      api.getHomeOffers,
      (d) => (this.offers = d || []),
    );
    this.loadSection(
      "sponsored",
      "home_sponsored",
      api.getHomeSponsored,
      (d) => (this.sponsored = d || []),
    );
    if (localStorage.getItem("token"))
      api
        .getProfileFeatures()
        .then(
          (r) =>
            (this.notificationCount = (r.data?.notifications || []).filter(
              (n) => !Number(n.is_read),
            ).length),
        )
        .catch(() => {});
    window.addEventListener("scroll", this.rememberScroll);
  },
  beforeUnmount() {
    this.rememberScroll();
    window.removeEventListener("scroll", this.rememberScroll);
  },
  methods: {
    async loadSection(key, cacheKey, request, apply) {
      const cached = getStored(cacheKey);
      if (cached) {
        apply(cached);
        this.loading[key] = false;
      }
      try {
        const r = await request();
        apply(r.data);
        setStored(cacheKey, r.data);
      } finally {
        this.loading[key] = false;
      }
    },
    mapStories(items) {
      const map = {
        news: ["📢", "#fff1f2", "Breaking News", true],
        job: ["💼", "#eef2ff", "Jobs"],
        event: ["📅", "#fff1f5", "Events"],
        business: ["🏢", "#eff6ff", "Business"],
        property: ["🏠", "#ecfdf5", "Property"],
        food: ["🍔", "#fff7ed", "Food"],
      };
      const stories = (items || []).map((x) => {
        const m = map[x.name] || ["✨", "#f5f3ff", x.name];
        return {
          name: m[2] || x.name,
          icon: m[0],
          color: m[1],
          type: x.name,
          live: m[3],
        };
      });
      stories.splice(3, 0, {
        name: "Offers",
        icon: "🏷️",
        color: "#fff1f2",
        type: "business",
      });
      return stories.concat([
        {
          name: "Emergency",
          icon: "🚨",
          route: "/emergencylist",
          color: "#fff1f2",
        },
      ]);
    },
    changeView(mode) {
      this.viewMode = mode;
      setListingView(mode);
    },
    changeCity() {
      const value = window.prompt("Enter city or area", this.city);
      if (value?.trim()) {
        this.city = value.trim();
        setStored("last_selected_location", this.city);
      }
    },
    openCategory(s) {
      this.$router.push(
        s.route || { path: "/list", query: { name: s.type, title: s.name } },
      );
    },
    openListing(item) {
      addRecentlyViewed(item);
      this.recentlyViewed = getRecentlyViewed();
      this.$router.push({
        path: "/details",
        query: { id: item.id, name: item.type || "business" },
      });
    },
    shareListing(item) {
  const type = item.offer ? "offer" : (item.type || "business");

  // Only used to construct the URL. Backend still looks up exclusively by lid.
  const slug = item.slug || "listing";

  const shareUrl = new URL(
    `/app/${encodeURIComponent(type)}/${encodeURIComponent(slug)}`,
    window.location.origin
  );

  shareUrl.searchParams.set("lid", item.id);
 
  if (navigator.share) {
    navigator.share({
      title: item.name || item.title || "MH13 Listing",
      text: "\n",
      url: shareUrl.toString()
    }).catch(() => {
      // Usually means the user cancelled sharing.
    });

    return;
  }

  navigator.clipboard
    ?.writeText(shareUrl.toString())
    .then(() => this.$alert("Share link copied", "success"));
},
    rememberScroll() {
      sessionStorage.setItem("scroll_home", String(window.scrollY));
    },
    restoreScroll() {
      this.$nextTick(() =>
        setTimeout(
          () =>
            window.scrollTo(
              0,
              Number(sessionStorage.getItem("scroll_home") || 0),
            ),
          0,
        ),
      );
    },
  },
};
</script>
<style scoped>
.home-header {
  height: 86px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 11px 13px;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 50;
  box-shadow: 0 3px 15px rgba(15, 23, 42, 0.04);
}

.menu-btn {
  width: 35px;
  border: 0;
  background: transparent;
  font-size: 27px;
  color: #0f172a;
}

.brand {
  min-width: 0;
  flex: 1;
}

.brand img {
  display: block;
  width: 102px;

  object-fit: contain;
  object-position: left;
}

.brand button {
  border: 0;
  background: transparent;
  padding: 2px 0;
  color: #334155;
  font-size: 11px;
  font-weight: 600;
}

.brand button i:first-child {
  color: #ef4444;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.header-actions button,
.header-actions a {
  position: relative;
  width: 43px;
  height: 43px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #f8fafc;
  color: #0f172a;
  text-decoration: none;
  font-size: 19px;
  box-shadow: 0 5px 14px rgba(15, 23, 42, 0.06);
}

.header-actions b {
  position: absolute;
  right: -1px;
  top: -3px;
  min-width: 18px;
  height: 18px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #ef4444;
  color: #fff;
  font-size: 8px;
}

.header-actions a span {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffb703, #ff5ba7);
  color: #fff;
  font-weight: 700;
}

.welcome {
  display: flex;
  align-items: center;
  gap: 10px;
  /* padding: 12px 2px */
}

.sun {
  font-size: 35px;
}

.welcome > div:nth-child(2) {
  min-width: 0;
  flex: 1;
}

.welcome h1 {
  margin: 0;
  font-size: 17px;
  line-height: 1.3;
}

.welcome p {
  margin: 3px 0 0;
  color: #64748b;
  font-size: 10px;
}

.weather {
  padding: 9px 10px;
  border-radius: 16px;
  background: linear-gradient(135deg, #f3f0ff, #fff);
  text-align: center;
}

.weather strong,
.weather small {
  display: block;
}

.weather strong {
  font-size: 14px;
}

.weather small {
  font-size: 8px;
  color: #64748b;
}

.mh-section-title i {
  color: #6d28d9;
}

.feed {
  display: grid;
  gap: 10px;
}
</style>
